import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent, CardComponent, SpinnerComponent, BadgeComponent,
  TabSetComponent, type Tab, EmptyStateComponent,
} from '@echospaces-web-monorepo/es-shared-ui';
import { IslandService } from './island.service';

@Component({
  selector: 'es-islands-detail',
  imports: [ButtonComponent, CardComponent, SpinnerComponent, BadgeComponent, TabSetComponent, EmptyStateComponent],
  template: `
    @if (islandService.loading()) {
      <div class="flex justify-center py-12">
        <es-spinner label="Loading island..."></es-spinner>
      </div>
    } @else if (islandService.selectedIsland(); as island) {
      <div class="max-w-4xl mx-auto">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <button class="text-sm text-[var(--text-secondary)] hover:text-echo-blue mb-2 cursor-pointer" (click)="back()">← Back to Islands</button>
            <h1 class="text-2xl font-bold font-exo">{{ island.name }}</h1>
            <div class="flex items-center gap-2 mt-1">
              <es-badge [label]="island.slug" variant="primary"></es-badge>
            </div>
          </div>
          <div class="flex gap-2">
            <es-button variant="secondary" size="sm" (clicked)="openSpaces()">Spaces</es-button>
            <es-button variant="primary" size="sm">Edit</es-button>
          </div>
        </div>

        <!-- Banner -->
        <div class="h-48 gradient-blue-violet rounded-xl mb-6 flex items-center justify-center">
          <span class="text-6xl opacity-40">🏝</span>
        </div>

        <!-- Tabs -->
        <es-tab-set [tabs]="tabs" (tabChanged)="activeTab.set($event)">
          @switch (activeTab()) {
            @case ('info') {
              <es-card>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="text-[var(--text-secondary)]">Slug</span>
                    <p class="font-medium">{{ island.slug }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Organization</span>
                    <p class="font-medium font-mono text-xs">{{ island.organizationId }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Description</span>
                    <p class="font-medium">{{ island.description || '—' }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Created</span>
                    <p class="font-medium">{{ island.createdAt }}</p>
                  </div>
                </div>
              </es-card>
            }
            @case ('maps') {
              <es-empty-state
                title="Maps"
                description="Unreal maps/levels within this island. Manage deployments and versions."
                icon="🗺"
                actionLabel="Create Map"
              ></es-empty-state>
            }
            @case ('deployments') {
              <es-empty-state
                title="Deployments"
                description="Map deployment lifecycle: LOCAL → TESTING → STAGING → PUBLISHED."
                icon="🚀"
              ></es-empty-state>
            }
          }
        </es-tab-set>
      </div>
    }
  `,
})
export class IslandDetailComponent implements OnInit {
  islandService = inject(IslandService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  activeTab = signal('info');

  tabs: Tab[] = [
    { id: 'info', label: 'Info', icon: 'ℹ' },
    { id: 'maps', label: 'Maps', icon: '🗺' },
    { id: 'deployments', label: 'Deployments', icon: '🚀' },
  ];

  ngOnInit() {
    const id = this.route.snapshot.params['islandId'];
    if (id) this.islandService.loadIsland(id);
  }

  back() {
    this.router.navigate(['/islands']);
  }

  openSpaces() {
    const id = this.islandService.selectedIsland()?.id;
    if (id) this.router.navigate(['/islands', id, 'spaces']);
  }
}
